import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { ReplaySubject, Subject } from 'rxjs';
import * as GetAccounts from '../../server/routes/auth/accounts';
import * as AuthLogout from '../../server/routes/auth/logout';
import { type SuperAdmin } from '../../server/services/account.service';
import { type AuthResult } from '../../server/services/auth.service';
import {
  generateId,
  sortByNameExactFirst,
} from '../../server/services/utils.service';
import type { Org, OrgUser } from '../../server/types/org-types';
import type { DeepReadonly } from '../../server/util-type';
import { UtilsService } from './utils.service';

// https://scotch.io/tutorials/protecting-angular-v2-routes-with-canactivatecanactivatechild-guards#toc-authentication-guard
// https://www.youtube.com/watch?v=WveRq-tlb6I
// https://www.youtube.com/watch?v=wswK6AzgvTE

export type UserOrgObj = {
  accountId: string;
  user: OrgUser;
  org: Org;
  superAdmins?: SuperAdmin[];
};

const routeAfterCompleteLogout = '/';

@Injectable()
export class AuthUserService {
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);
  private utils = inject(UtilsService);
  private cookieService = inject(CookieService);

  appInstanceId = generateId();
  private authInitStarted = false;
  authInitComplete = false;

  private isLoggedInStore: boolean | undefined;
  private accountStore: AuthResult | undefined;
  private allAccountsStore: AuthResult[] = [];
  private userOrgObjStore: UserOrgObj | undefined;
  private allUserOrgObjsStore: UserOrgObj[] = [];

  // newAuth: If we go from a state of not logged in to logged in
  private isLoggedInSubject = new ReplaySubject<boolean>();
  isLoggedIn = this.isLoggedInSubject.asObservable();

  private accountSubject = new ReplaySubject<AuthResult | undefined>(1);
  account = this.accountSubject.asObservable();

  private allAccountsSubject = new ReplaySubject<AuthResult[] | undefined>(1);
  allAccounts = this.allAccountsSubject.asObservable();

  private userOrgObjSubject = new ReplaySubject<UserOrgObj | undefined>(1);
  userOrgObj = this.userOrgObjSubject.asObservable();

  private allUserOrgObjsSubject = new ReplaySubject<UserOrgObj[]>(1);
  allUserOrgObjs = this.allUserOrgObjsSubject.asObservable();

  authInit(): void {
    // Prevent authInit being called twice
    if (this.authInitStarted) {
      return;
    } else {
      this.authInitStarted = true;
    }

    // Refresh sessions
    this.refreshSession().subscribe({
      next: () => {
        this.authInitComplete = true;
      },
      error: () => {
        this.authInitComplete = true;
      },
    });
  }

  /**
   * @param forceAccountId Used at login/register
   */
  decideUserAccountToSelect(forceAccountId?: string) {
    // Select user
    const selectedUserId =
      this.userOrgObjStore?.user._id ??
      localStorage.getItem('auth.selectedUserId');

    // Select account
    const setAccountIdAfterLogIn = this.cookieService.get(
      'setAccountIdAfterLogIn'
    );
    const selectedAccountId =
      setAccountIdAfterLogIn ??
      forceAccountId ?? // When login in or register occurs we want to force this accountId
      this.accountStore?.accountId ??
      localStorage.getItem('auth.selectedAccountId');

    const previouslySelectedUserOrgObj = selectedUserId
      ? this.allUserOrgObjsStore.find(
          (userOrgObj) =>
            userOrgObj.user._id === selectedUserId &&
            // If we are forcing an accountId, don't allow userIds which aren't
            // for the accountId
            (!forceAccountId || userOrgObj.accountId !== forceAccountId)
        )
      : undefined;

    // Prioritize user selection, but only if the accountId is not set in the cookie after login
    if (
      !setAccountIdAfterLogIn &&
      selectedUserId &&
      previouslySelectedUserOrgObj
    ) {
      // If selected user in the localStorage and part of auth result
      this.setUser(selectedUserId);
    } else if (selectedAccountId) {
      // If the accountId is set, try to get the first user
      const userId = this.allUserOrgObjsStore.find(
        (u) => u.accountId === selectedAccountId
      )?.user._id;

      const accountInAccountStore = this.allAccountsStore.find(
        (a) => a.accountId === selectedAccountId
      );
      if (userId) {
        this.selectUser(userId);
      } else if (accountInAccountStore) {
        this.setAccount(selectedAccountId);
        this.markUserUndefined();
      } else {
        // select first in list
        if (this.allAccountsStore[0]) {
          this.selectAccount(this.allAccountsStore[0].accountId);
        } else {
          // No account error, should automatically
          this.selectAccount('');
        }
      }
    } else if (this.allUserOrgObjsStore[0]) {
      // If there is at least one org object, select the first one
      this.selectUser(this.allUserOrgObjsStore[0].user._id);
    } else if (this.allAccountsStore[0]) {
      this.selectAccount(this.allAccountsStore[0].accountId);
    } else {
      this.markUserUndefined();
      this.markAccountUndefined();
    }

    if (setAccountIdAfterLogIn)
      this.cookieService.delete('setAccountIdAfterLogIn');

    return { userOrgObj: this.userOrgObjStore, account: this.accountStore };
  }

  // This can be called at-will
  refreshSession() {
    const complete = new Subject<boolean>();
    this.http.get<GetAccounts.ResBody>('/api/accounts').subscribe({
      next: (accounts) => {
        // Replace existing data
        this.allAccountsStore = accounts;
        this.allUserOrgObjsStore = _.flatten(
          accounts.map((a) => this.createUserOrgObjs(a))
        );
        this.isLoggedInStore = true;

        this.decideUserAccountToSelect();

        // Update subjects
        this.allAccountsSubject.next(this.allAccountsStore);
        this.allUserOrgObjsSubject.next(this.allUserOrgObjsStore);
        this.isLoggedInSubject.next(!!this.isLoggedInStore);

        complete.next(true);
      },
      error: (err) => {
        if (err.status === 401) {
          // Replace existing data
          this.allAccountsStore = [];
          this.allUserOrgObjsStore = [];
          this.accountStore = undefined;
          this.userOrgObjStore = undefined;
          this.isLoggedInStore = false;

          // Update subjects
          this.userOrgObjSubject.next(undefined);
          this.accountSubject.next(undefined);
          this.allAccountsSubject.next(this.allAccountsStore);
          this.allUserOrgObjsSubject.next(this.allUserOrgObjsStore);
          this.isLoggedInSubject.next(!!this.isLoggedInStore);
          complete.next(true);
          return;
        }

        if (err.status === 0) {
          this.utils.launchOfflineNotification();
          return; // Don't clean up
        }

        complete.error(err);
      },
    });
    return complete;
  }

  private createUserOrgObjs({
    accountId,
    orgs,
    superAdmins,
  }: AuthResult): UserOrgObj[] {
    const result = orgs.map((org) =>
      this.createUserOrgObj({ accountId, org, superAdmins })
    );
    return result;
  }

  private createUserOrgObj({
    accountId,
    org,
    superAdmins,
  }: {
    accountId: string;
    org: Org;
    superAdmins: SuperAdmin[] | undefined;
  }): UserOrgObj {
    const user = org.users.find((u) => u.accountId === accountId);
    if (!user) throw new Error('accountId not found in org');
    return _.cloneDeep({ accountId, user, org, superAdmins });
  }

  processCreateOrg({ accountId, org }: { accountId: string; org: Org }) {
    // Update account entries
    let account = this.allAccountsStore.find((a) => a.accountId === accountId);
    if (!account) {
      console.error('accountId not found in processCreateOrg');
      this.snackBar.open('Account could not be found (create org)', 'Dismiss', {
        duration: 5000,
      });
      account = this.allAccountsStore.find(
        ({ accountId: _id }) => _id === this.accountStore?.accountId
      );
      if (!account) account = this.allAccountsStore[0];
      if (!account) {
        console.error('No accounts found whatsoever in processCreateOrg');
        return;
      }
    }
    account.orgs.push(org);
    // Here we are checking if the account is being stored by reference or if a deep copy is made
    if (account !== this.accountStore) this.accountStore?.orgs.push(org);

    // Update the userOrgs
    const userOrgObj = this.createUserOrgObj({
      accountId,
      org,
      superAdmins: account.superAdmins,
    });
    this.allUserOrgObjsStore.push(userOrgObj);
    this.userOrgObjStore = userOrgObj;

    // Update subjects
    this.allAccountsSubject.next(this.allAccountsStore);
    this.allUserOrgObjsSubject.next(this.allUserOrgObjsStore);
    this.accountSubject.next(this.accountStore);
    this.userOrgObjSubject.next(this.userOrgObjStore);

    // Select self userId
    this.selectUser(userOrgObj.user._id);
  }

  updateOrg({ org }: { org: Org }) {
    let accountId: string | undefined = undefined;
    let index: number;
    // Try to add to org associated with current accountId
    if (this.userOrgObjStore && org._id === this.userOrgObjStore.org._id) {
      accountId = this.userOrgObjStore.accountId;
      index = this.allUserOrgObjsStore.findIndex(
        (o) => o.accountId === accountId && o.org._id === org._id
      );
    } else {
      // Otherwise just update the first one
      index = this.allUserOrgObjsStore.findIndex((o) => o.org._id === org._id);
      accountId = this.allUserOrgObjsStore[index].accountId;
    }
    if (index === -1) return;

    const { superAdmins } = this.allUserOrgObjsStore[index];

    const userOrgObj = this.createUserOrgObj({ accountId, org, superAdmins });
    this.allUserOrgObjsStore.splice(index, 1, userOrgObj);
    this.allUserOrgObjsSubject.next(this.allUserOrgObjsStore);

    if (this.userOrgObjStore?.org._id === userOrgObj.org._id) {
      this.userOrgObjStore = userOrgObj;
      this.userOrgObjSubject.next(this.userOrgObjStore);
    }
  }

  private setAccount(accountId: string) {
    let account = this.allAccountsStore.find(
      ({ accountId: _id }) => _id === accountId
    );
    if (!account) {
      console.error('accountId not found in setAccount');
      this.snackBar.open(
        'Account could not be found (set account)',
        'Dismiss',
        {
          duration: 5000,
        }
      );
      account = this.allAccountsStore.find(
        ({ accountId: _id }) => _id === this.accountStore?.accountId
      );
      if (!account) account = this.allAccountsStore[0];
      if (!account) {
        throw new Error('No accounts found whatsoever in setAccount');
      }
    }

    this.accountStore = account;
    this.accountSubject.next(account);
    return account.accountId; // might be dfferent from the input
  }

  selectAccount(accountId: string) {
    const actualAccountId = this.setAccount(accountId);
    localStorage.setItem('auth.selectedAccountId', actualAccountId);
    const userOrgAvailable = this.allUserOrgObjsStore.find(
      (u) => u.accountId === actualAccountId
    );

    if (userOrgAvailable) {
      this.setUser(userOrgAvailable.user._id);
    } else {
      this.markUserUndefined();
    }
  }

  private markAccountUndefined() {
    this.accountStore = undefined;
    this.accountSubject.next(undefined);
    localStorage.removeItem('auth.selectedAccountId');
  }

  private setUser(userId: string) {
    const userOrgObj = this.allUserOrgObjsStore.find(
      (u) => u.user._id === userId
    );
    if (!userOrgObj) throw new Error('userId not found');

    this.setAccount(userOrgObj.accountId);

    this.userOrgObjStore = userOrgObj;
    this.userOrgObjSubject.next(userOrgObj);
  }

  selectUser(userId: string) {
    this.setUser(userId);
    localStorage.setItem('auth.selectedUserId', userId);
  }

  private markUserUndefined() {
    this.userOrgObjStore = undefined;
    this.userOrgObjSubject.next(undefined);
    localStorage.removeItem('auth.selectedUserId');
  }

  getAllAuthUsers() {
    return this.allUserOrgObjsStore;
  }

  getAccount(accountId?: string) {
    if (!accountId) return this.accountStore;
    return this.allAccountsStore.find((a) => a.accountId === accountId);
  }

  getAuthUserId({ orgId }: { orgId?: string }) {
    let userId: string | undefined;
    if (orgId) {
      userId = this.allUserOrgObjsStore.find((u) => u.org._id === orgId)?.user
        ._id;
    } else {
      userId = this.userOrgObjStore?.user._id;
    }
    if (!userId) throw new Error('org not found or not logged in');
    return userId;
  }

  getAuthUserOrgObj({
    orgId,
    accountId,
  }: {
    orgId?: string;
    accountId?: string;
  }) {
    let userOrgObj: UserOrgObj | undefined;
    if (orgId) {
      userOrgObj = this.allUserOrgObjsStore.find((u) => u.org._id === orgId);
    } else if (accountId) {
      userOrgObj = this.allUserOrgObjsStore.find(
        (u) => u.accountId === accountId
      );
    } else {
      userOrgObj = this.userOrgObjStore;
    }
    if (!userOrgObj) throw new Error('org not found or not logged in');
    return userOrgObj;
  }

  getUser({ orgId, userId }: { orgId?: string; userId: string }) {
    orgId = orgId ?? this.userOrgObjStore?.org._id;
    if (!orgId) throw new Error('orgId not found');
    const userOrgObj = this.allUserOrgObjsStore.find(
      (u) => u.org._id === orgId
    );
    if (!userOrgObj) throw new Error('orgId not found');
    return userOrgObj?.org.users.find((u) => u._id === userId);
  }

  getSpace({ orgId, spaceId }: { orgId: string; spaceId: string }) {
    const userOrgObj = this.allUserOrgObjsStore.find(
      (u) => u.org._id === orgId
    );
    if (!userOrgObj) throw new Error('org not found');
    const space = userOrgObj.org.spaces.find((s) => s._id === spaceId);
    if (!space) throw new Error('space not found');
    return space;
  }

  getSpaceUserData({
    orgId,
    spaceId,
  }: {
    orgId: string;
    spaceId: string;
  }): DeepReadonly<OrgUser>[] {
    const userOrgObj = this.allUserOrgObjsStore.find(
      (u) => u.org._id === orgId
    );
    if (!userOrgObj) throw new Error('org not found');
    const space = userOrgObj.org.spaces.find((s) => s._id === spaceId);
    if (!space) throw new Error('space not found');

    const users: DeepReadonly<OrgUser>[] = [];
    space.users.forEach(({ _id }) => {
      const user = userOrgObj.org.users.find((u) => u._id === _id);
      if (user) users.push(user);
    });
    return users;
  }

  processRegister(authResult: AuthResult) {
    this.addAuthResult(authResult);
    this.decideUserAccountToSelect(authResult.accountId);
  }

  processLogin(authResult: AuthResult) {
    this.addAuthResult(authResult);
    const { account, userOrgObj } = this.decideUserAccountToSelect(
      authResult.accountId
    );

    if (!account) throw new Error('No account found after successful login');
    return { account, userOrgObj };
  }

  processAcceptInvite(authResult: AuthResult) {
    this.addAuthResult(authResult);
    this.decideUserAccountToSelect(authResult.accountId);
  }

  private addAuthResult(authResult: AuthResult) {
    const previouslyLoggedOut = !this.accountStore;
    const existing = this.allAccountsStore.find(
      (a) => a.accountId === authResult.accountId
    );
    if (existing) throw new Error('accountId already added'); // this should never happen

    const userOrgObjs = this.createUserOrgObjs(authResult);
    userOrgObjs.forEach((u) => this.allUserOrgObjsStore.push(u));
    this.allAccountsStore.push(authResult);

    this.allAccountsSubject.next(this.allAccountsStore);
    this.allUserOrgObjsSubject.next(this.allUserOrgObjsStore);

    if (previouslyLoggedOut) this.isLoggedInSubject.next(true);
  }

  logOut({ accountId }: { accountId?: string }): void {
    const body: AuthLogout.ReqBody = { accountId };
    this.http.post<AuthLogout.ResBody>('/api/auth/logout', body).subscribe({
      next: () => {
        this.refreshSession().subscribe(() => {
          // If completely logged out
          if (!this.isLoggedInStore)
            this.router.navigateByUrl(routeAfterCompleteLogout);
        });
      },
      error: (errorResponse) => {
        this.snackBar.open('Problem logging out', 'Dismiss');
      },
    });
  }

  searchUsers({ orgId, value }: { orgId: string; value: string }) {
    const orgUser = this.allUserOrgObjsStore.find((u) => u.org._id === orgId);
    if (!orgUser) throw new Error('orgId not found in searchUser');
    const users = orgUser.org.users.filter(
      (u) =>
        u.name.toLowerCase().includes(value.toLowerCase()) ||
        u.email.toLowerCase().includes(value.toLowerCase())
    );

    const startsWithUsers: OrgUser[] = [];
    const otherUsers: OrgUser[] = [];

    users.forEach((u) => {
      if (u.name.toLowerCase().startsWith(value.toLowerCase()))
        startsWithUsers.push(u);
      else otherUsers.push(u);
    });

    users.sort(sortByNameExactFirst(value));

    return users;
  }
}

export function unknownUser({ userId }: { userId: string }): OrgUser {
  const user: OrgUser = {
    _id: userId,
    accountId: '',
    name: 'Unknown user',
    email: '',
    color: 0,
    admin: false,
    createdAt: 0,
    updatedAt: 0,
  };
  return user;
}
